import styled from "styled-components/macro";
import { CircularProgress } from "@mui/material";

import { getPredictedFutureMetric } from "../../utils/metrics";
import {
  useSingleProcessFullRangeDataDump,
  useRollupDisplayForProcess,
  useDashboardPageContext,
  useSelectedShift,
} from "../../utils/customHooks/genericHooks";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { calcMetric } from "../../utils/metrics";
import { DataDumpAggregateType } from "types/types";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { clamp } from "lodash";
import { formatHoursMinutes } from "utils/timeUtils";

const MetricStyles = styled.div`
  font-size: 10pt;
  flex-grow: 1;
`;

type ProcessMetricsProps = {
  dataDumpKey: keyof DataDumpAggregateType;
  processId: string;
  unit: string;
  decimalPoints?: number;
  shouldFormatHoursMinutes?: boolean;
};
export const ProcessMetrics = ({
  dataDumpKey,
  processId,
  unit,
  decimalPoints,
  shouldFormatHoursMinutes,
}: ProcessMetricsProps) => {
  const { isPredictedFutureMetric, estimatedFutureMetricPercent } =
    useSettings();
  const { isFullDaySelected } = useDashboardPageContext();
  const rollupMetric = useRollupDisplayForProcess(processId);
  const selectedShift = useSelectedShift();
  const singleProcessDataDump = useSingleProcessFullRangeDataDump(processId);
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const isDataAvailable = Boolean(
    isMidgardDDAEnabled ? singleProcessDataDump : rollupMetric
  );

  const shouldShowPredictedMetric =
    isPredictedFutureMetric && selectedShift?.range?.lt > Date.now();

  const metricForThisTimeline = !isDataAvailable
    ? undefined
    : shouldShowPredictedMetric
      ? (getPredictedFutureMetric({
          isOver24Hours: isFullDaySelected,
          dataDumpKey,
          selectedShift,
          rollupDisplay: rollupMetric,
          numProcesses: 1,
          estimatedFutureMetricPercent,
          dataDumpsObjForFullRange: singleProcessDataDump,
          isMidgardDDAEnabled,
          getNumbers: true,
        }) as number | undefined)
      : calcMetric({
          rollupMetric,
          dataDumpKey,
          dataDumpsObjForRange: singleProcessDataDump,
          isMidgardDDAEnabled,
        });

  let metricDisplay =
    metricForThisTimeline !== undefined
      ? metricForThisTimeline.toFixed(clamp(decimalPoints ?? 0, 0, 6)) + unit
      : "--";

  if (shouldFormatHoursMinutes && metricForThisTimeline) {
    metricDisplay = formatHoursMinutes(metricForThisTimeline);
  }

  return (
    <MetricStyles
      data-server-name={
        dataDumpKey === "oeePercent"
          ? [SERVER_NAMES.midgardDataDumpsFullRangeByProcess]
          : []
      }
    >
      {!metricDisplay ? <CircularProgress size={12} /> : metricDisplay}
    </MetricStyles>
  );
};

export default ProcessMetrics;
